import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Radio,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useSearch } from "../../hooks/UseSearch";
import SimpleSearch from "../common/SimpleSearch";
import { useTranslation } from "react-i18next";
import { useServiceModules } from "../../store/selectors/serviceModulesSelector";
import { GeneralButton } from "../../common/ui/GeneralButton";
import ActionButton from "../common/table/ActionButton";
import { ActionListItemStyles, StyledDialogListBox } from "../actionModules/SelectActionModulesDialog.styles";
import { ServiceModule, ServiceModuleType } from "../../types/serviceModuleTypes";
import { useAppDispatch } from "../../hooks/StoreHooks";
import { fetchServiceModulesThunk } from "../../store/thunk/serviceModulesThunk";

interface Props {
    onClose: () => void;
    selectedModule: number | null;
    moduleType?: ServiceModuleType;
    handleOnSave: (newSelectedModule: number) => void;
}

export default function SelectSingleServiceModuleDialog(props: Props) {
    const { searchQuery, handleOnSearch } = useSearch();
    const serviceModules = useServiceModules();
    const { t } = useTranslation();

    const [selectedModuleId, setSelectedModuleId] = useState<number | null>(props.selectedModule);

    const filteredModules = useMemo(
        () => {
            let modules = serviceModules;

            modules = props.moduleType
                ? modules.filter((m) => m.type === props.moduleType)
                : modules;

            modules = searchQuery
                ? modules.filter((m) => m.name.toLowerCase().includes(searchQuery.toLowerCase()))
                : modules;

            return modules;
        },
        [searchQuery, serviceModules]
    );

    useEffect(() => {
        setSelectedModuleId(props.selectedModule);
    }, [props.selectedModule]);

    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(fetchServiceModulesThunk());
    }, [dispatch]);

    const moduleItems = filteredModules.map((item) => (
        <ServiceModuleListItem
            key={item.id}
            item={item}
            isSelected={selectedModuleId === item.id}
            onSelect={() => setSelectedModuleId(item.id)}
        />
    ));

    return (
        <>
            <Dialog
                open={true}
                onClose={props.onClose}
                fullWidth
                maxWidth="sm"
                slotProps={{
                    paper: { sx: { padding: 2, borderRadius: 3 } },
                }}
            >
                <DialogTitle>{t("groupsPage.groups.selectServiceModules")}</DialogTitle>
                <DialogContent dividers>
                    <SimpleSearch
                        searchQuery={searchQuery}
                        onSearchHandler={handleOnSearch}
                    />
                    <StyledDialogListBox>
                        {moduleItems}
                    </StyledDialogListBox>
                </DialogContent>
                <DialogActions>
                    <GeneralButton
                        appVariant="secondary"
                        onClick={props.onClose}
                    >
                        {t("buttons.cancel")}
                    </GeneralButton>
                    <GeneralButton
                        disabled={selectedModuleId === null}
                        onClick={() => selectedModuleId !== null && props.handleOnSave(selectedModuleId)}
                    >
                        {t("buttons.apply")}
                    </GeneralButton>
                </DialogActions>
            </Dialog>
        </>
    );
}


interface ServiceModuleListItemProps {
    item: ServiceModule;
    isSelected: boolean;
    onSelect: () => void;
}

function ServiceModuleListItem({ item, isSelected, onSelect }: ServiceModuleListItemProps) {
    return (
        <ActionListItemStyles>
            <Box>
                <ActionButton
                    iconName={item.iconName}
                    colorHex={item.colorHex ? item.colorHex : ""}
                    iconUrl={item.iconUrl}
                    actionName={item.name}
                />
                <Typography>{item.name}</Typography>
            </Box>
            <Radio
                checked={isSelected}
                onChange={onSelect}
                sx={(theme) => ({
                    color: theme.palette.companyMain.main,
                })}
                slotProps={{ input: { "aria-label": "controlled" } }}
            />
        </ActionListItemStyles>
    );
}
